<!--
  The view layer, on screen.

  Everything here used to be invisible from the web: the view name was a private const in the BAL,
  the SQL was whatever the last refresh happened to write, and the join rules were a hard-coded
  dictionary. When a fund did not show up in the app there was nothing to look at, so the fund node
  got the blame for a view that was stale or a table that was never joined.

  Two halves:
   • RELATIONS — what each import table does in the joined view. Editable; the server regenerates
     the SQL from it, so there is no way to write SQL that does not compile.
   • SQL — what the database is running, next to what this build would write. They differ when the
     view is stale or was hand-edited, and that gap is the whole reason both are shown.
-->
<template>
  <v-dialog :model-value="modelValue" max-width="1200" scrollable @update:model-value="$emit('update:modelValue', $event)">
    <v-card class="mfvl-card">
      <v-card-title class="mfvl-header">
        <div class="d-flex align-center" style="gap: 12px">
          <div class="mfvl-header-icon"><v-icon size="24">mdi-database-search</v-icon></div>
          <div>
            <div class="mfvl-title">View layer</div>
            <div class="mfvl-subtitle">
              วิวที่โหนด Mutual Fund อ่าน · ประกอบจากตาราง import ตัวไหนบ้าง · SQL ที่รันอยู่จริง
            </div>
          </div>
        </div>
        <v-btn icon="mdi-close" variant="text" aria-label="Close view layer" @click="close" />
      </v-card-title>

      <v-progress-linear v-if="loading || saving" indeterminate color="primary" height="3" />
      <v-divider v-else />

      <v-card-text class="mfvl-body">
        <v-alert v-if="errorMessage" type="error" variant="tonal" density="comfortable" class="mb-4">
          {{ errorMessage }}
        </v-alert>

        <!-- ══ the views themselves ═══════════════════════════════════════════════════════════ -->
        <div class="mfvl-section-label">Views</div>
        <v-row dense class="mb-2">
          <v-col v-for="view in detail.views" :key="view.name" cols="12" md="4">
            <v-card variant="outlined" class="mfvl-view-card" :class="{ 'mfvl-view-missing': !view.exists }">
              <div class="d-flex align-center justify-space-between">
                <code class="mfvl-view-name">{{ view.name }}</code>
                <v-chip v-if="view.name === detail.allSourcesView" size="x-small" color="primary" variant="flat">
                  โหนดอ่านตัวนี้
                </v-chip>
              </div>
              <div v-if="view.exists" class="mfvl-view-stats">
                {{ formatNumber(view.rowCount) }} แถว · {{ view.columnCount }} คอลัมน์
              </div>
              <div v-else class="mfvl-view-stats mfvl-warn">ยังไม่ถูกสร้าง — กด Refresh</div>
              <div v-if="view.error" class="mfvl-view-stats mfvl-warn">{{ view.error }}</div>
            </v-card>
          </v-col>
        </v-row>

        <!-- Stale views are the failure this dialog exists to make visible: the app reads SQL that
             no longer matches the code, and nothing anywhere says so. -->
        <v-alert v-if="isStale" type="warning" variant="tonal" density="comfortable" class="mb-4">
          SQL ที่ฐานข้อมูลรันอยู่ <strong>ไม่ตรง</strong> กับที่โค้ดรุ่นนี้จะสร้าง — วิวเก่ากว่าโค้ด หรือถูกแก้ด้วยมือ
          กด <strong>Refresh views</strong> เพื่อสร้างใหม่
        </v-alert>

        <!-- ══ relations ══════════════════════════════════════════════════════════════════════ -->
        <div class="mfvl-section-label d-flex align-center justify-space-between">
          <span>กติกาการ join</span>
          <span v-if="!detail.relationsAreOverridable" class="mfvl-readonly-note">
            อ่านอย่างเดียว — ยังไม่ได้สร้างตาราง MutualFundViewRelation
          </span>
        </div>
        <div class="mfvl-hint mb-2">
          <strong>Anchor</strong> = ตารางที่ถือ "กองทุน" หนึ่งแถวต่อกอง ·
          <strong>Satellite</strong> = มีหลายแถวต่อกอง จะถูกยุบเหลือแถวเดียวก่อน join ·
          <strong>Skip</strong> = ไม่ join (คอลัมน์ยังอยู่ในวิวแต่เป็น NULL)
        </div>

        <v-table density="compact" class="mfvl-table">
          <thead>
            <tr>
              <th style="width: 26%">ตาราง import</th>
              <th style="width: 15%">บทบาท</th>
              <th style="width: 19%">join ด้วยคอลัมน์</th>
              <th style="width: 28%">เรียงลำดับด้วย</th>
              <th style="width: 12%">เก็บแถว</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="row in rows" :key="row.integrationId">
              <td>
                <div class="mfvl-source-name">{{ row.tableName }}</div>
                <div class="mfvl-source-meta">
                  #{{ row.integrationId }} · {{ row.columns.length }} คอลัมน์
                  <v-chip v-if="row.isOverridden" size="x-small" color="warning" variant="tonal" class="ml-1">
                    แก้ไว้
                  </v-chip>
                </div>
              </td>
              <td>
                <v-select v-model="row.role" :items="roleItems" density="compact" variant="outlined"
                  hide-details :disabled="!canEdit" />
              </td>
              <td>
                <v-select v-model="row.joinColumn" :items="row.columns" density="compact" variant="outlined"
                  hide-details clearable :disabled="!canEdit || row.role === 'Skip'" />
              </td>
              <td>
                <!-- Satellites only: an anchor is one row per fund already, so there is nothing to rank. -->
                <v-select v-model="row.orderColumns" :items="row.columns" density="compact" variant="outlined"
                  hide-details multiple chips closable-chips
                  :disabled="!canEdit || row.role !== 'Satellite'" />
              </td>
              <td>
                <v-select v-model="row.keepNewest" :items="keepItems" density="compact" variant="outlined"
                  hide-details :disabled="!canEdit || row.role !== 'Satellite'" />
              </td>
            </tr>
          </tbody>
        </v-table>

        <v-alert v-if="validationMessage" type="warning" variant="tonal" density="compact" class="mt-3">
          {{ validationMessage }}
        </v-alert>

        <!-- ══ SQL ════════════════════════════════════════════════════════════════════════════ -->
        <div class="mfvl-section-label mt-6">SQL</div>
        <v-tabs v-model="sqlTab" density="compact" class="mb-2">
          <v-tab value="installed">ที่รันอยู่จริง</v-tab>
          <v-tab value="generated">ที่โค้ดรุ่นนี้จะสร้าง</v-tab>
        </v-tabs>
        <pre class="mfvl-sql">{{ sqlTab === 'installed' ? (detail.installedSql || '— ยังไม่มีวิว —') : (detail.generatedSql || '') }}</pre>
      </v-card-text>

      <v-divider />
      <v-card-actions class="mfvl-actions">
        <v-btn variant="text" prepend-icon="mdi-refresh" :loading="refreshing" :disabled="saving" @click="refreshViews">
          Refresh views
        </v-btn>
        <v-spacer />
        <v-btn variant="text" @click="close">ปิด</v-btn>
        <v-btn color="primary" variant="elevated" :loading="saving" :disabled="!canEdit || !isDirty"
          @click="save">
          บันทึกและสร้างวิวใหม่
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
const ROLE_ITEMS = [
  { title: 'Anchor', value: 'Anchor' },
  { title: 'Satellite', value: 'Satellite' },
  { title: 'Skip', value: 'Skip' },
];

const KEEP_ITEMS = [
  { title: 'ล่าสุด', value: true },
  { title: 'แรกสุด', value: false },
];

export default {
  name: 'MutualFundViewLayerDialog',
  props: {
    modelValue: { type: Boolean, default: false },
  },
  emits: ['update:modelValue'],
  data() {
    return {
      loading: false,
      saving: false,
      refreshing: false,
      errorMessage: '',
      sqlTab: 'installed',
      detail: { views: [], sources: [], relationsAreOverridable: false, isStale: false },
      rows: [],
      // The rows exactly as they were loaded, so "is there anything to save?" is a comparison and
      // not a flag somebody has to remember to set.
      baseline: '[]',
    };
  },
  computed: {
    canEdit() {
      return this.detail.relationsAreOverridable && !this.loading && !this.saving;
    },
    isDirty() {
      return JSON.stringify(this.rows) !== this.baseline;
    },
    // The API decides this. Comparing installedSql to generatedSql here is what the screen used to do and it was
    // always true: installed is a whole CREATE VIEW statement, generated is only the body it wraps.
    isStale() {
      return Boolean(this.detail.isStale);
    },
    roleItems() { return ROLE_ITEMS; },
    keepItems() { return KEEP_ITEMS; },
    validationMessage() {
      const anchors = this.rows.filter((row) => row.role === 'Anchor');
      if (anchors.length === 0) {
        return 'ไม่มี Anchor เลย — วิวจะกลับไปเป็น UNION ALL (หนึ่งแถวต่อหนึ่งเรคอร์ด ไม่ใช่ต่อหนึ่งกอง)';
      }
      const missingKey = this.rows.find((row) => row.role !== 'Skip' && !row.joinColumn);
      if (missingKey) {
        return `${missingKey.tableName}: ยังไม่ได้เลือกคอลัมน์ที่ใช้ join`;
      }
      const noOrder = this.rows.find((row) => row.role === 'Satellite' && row.orderColumns.length === 0);
      if (noOrder) {
        return `${noOrder.tableName}: ไม่ได้เลือกคอลัมน์เรียงลำดับ จะใช้ลำดับแถวใน DB ซึ่งไม่การันตีว่าได้แถวไหน`;
      }
      return '';
    },
  },
  watch: {
    modelValue(open) {
      if (open) this.load();
    },
  },
  methods: {
    close() { this.$emit('update:modelValue', false); },
    formatNumber(value) { return Number(value || 0).toLocaleString(); },

    load() {
      this.loading = true;
      this.errorMessage = '';
      this.$call.GetMutualFundViewLayer(
        (response) => {
          // The $call mixin hands back the ResponseModel itself — status 0 is success, the payload is `data`.
          if (!response || response.status !== 0) {
            this.errorMessage = (response && response.message) || 'อ่านข้อมูล view layer ไม่สำเร็จ';
            this.loading = false;
            return;
          }
          const data = response.data || {};
          this.detail = {
            views: data.views || [],
            sources: data.sources || [],
            allSourcesView: data.allSourcesView,
            installedSql: data.installedSql,
            generatedSql: data.generatedSql,
            isStale: Boolean(data.isStale),
            relationsAreOverridable: Boolean(data.relationsAreOverridable),
          };
          this.rows = (data.sources || []).map((source) => ({
            integrationId: source.integrationId,
            tableName: source.tableName,
            columns: source.columns || [],
            role: source.role || 'Skip',
            joinColumn: source.joinColumn || null,
            orderColumns: source.orderColumns || [],
            keepNewest: source.keepNewest !== false,
            isOverridden: Boolean(source.isOverridden),
          }));
          this.baseline = JSON.stringify(this.rows);
          this.loading = false;
        },
        (error) => {
          this.errorMessage = this.readError(error, 'อ่านข้อมูล view layer ไม่สำเร็จ');
          this.loading = false;
        },
      );
    },

    save() {
      this.saving = true;
      this.errorMessage = '';
      this.$call.SaveMutualFundViewRelations(
        {
          relations: this.rows.map((row) => ({
            integrationId: row.integrationId,
            tableName: row.tableName,
            role: row.role,
            joinColumn: row.joinColumn,
            orderColumns: row.orderColumns,
            keepNewest: row.keepNewest,
          })),
        },
        (response) => {
          this.saving = false;
          if (response && response.status !== 0) {
            this.errorMessage = response.message || 'บันทึกไม่สำเร็จ';
            return;
          }
          // The save rebuilt the views, so everything on screen is now one refresh behind itself.
          this.load();
        },
        (error) => {
          this.errorMessage = this.readError(error, 'บันทึกไม่สำเร็จ');
          this.saving = false;
        },
      );
    },

    refreshViews() {
      this.refreshing = true;
      this.errorMessage = '';
      this.$call.RefreshMutualFundViewLayer(
        (response) => {
          this.refreshing = false;
          if (response && response.status !== 0) {
            this.errorMessage = response.message || 'สร้างวิวใหม่ไม่สำเร็จ';
            return;
          }
          this.load();
        },
        (error) => {
          this.errorMessage = this.readError(error, 'สร้างวิวใหม่ไม่สำเร็จ');
          this.refreshing = false;
        },
      );
    },

    /// Same shape the other Mutual Fund dialogs read failures in: a bare string body, or one with a message.
    readError(error, fallback) {
      const body = error && error.response ? error.response.data : null;
      if (typeof body === 'string' && body.trim()) return body.trim();
      if (body && typeof body.message === 'string' && body.message.trim()) return body.message.trim();
      return fallback;
    },
  },
};
</script>

<style scoped>
.mfvl-card { border-radius: 14px; }

.mfvl-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px;
}

.mfvl-header-icon {
  width: 44px;
  height: 44px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(118, 35, 126, .1);
  color: #76237E;
}

.mfvl-title { font-size: 1.05rem; font-weight: 700; }
.mfvl-subtitle { font-size: .78rem; color: rgba(0, 0, 0, .6); }
.mfvl-body { padding: 20px; }

.mfvl-section-label {
  font-size: .8rem;
  font-weight: 700;
  letter-spacing: .04em;
  text-transform: uppercase;
  color: rgba(0, 0, 0, .55);
  margin-bottom: 8px;
}

.mfvl-readonly-note { font-size: .72rem; font-weight: 500; text-transform: none; color: #B26A00; }
.mfvl-hint { font-size: .78rem; color: rgba(0, 0, 0, .6); }

.mfvl-view-card { padding: 12px; border-radius: 10px; }
.mfvl-view-missing { border-color: #E0A800; }
.mfvl-view-name { font-size: .82rem; font-weight: 600; }
.mfvl-view-stats { font-size: .75rem; color: rgba(0, 0, 0, .6); margin-top: 4px; }
.mfvl-warn { color: #B26A00; }

.mfvl-table :deep(td) { padding-top: 6px; padding-bottom: 6px; }
.mfvl-source-name { font-weight: 600; font-size: .85rem; }
.mfvl-source-meta { font-size: .72rem; color: rgba(0, 0, 0, .55); }

.mfvl-sql {
  max-height: 320px;
  overflow: auto;
  background: #1e1e1e;
  color: #d4d4d4;
  padding: 12px;
  border-radius: 8px;
  font-size: .74rem;
  line-height: 1.45;
  white-space: pre;
}

.mfvl-actions { padding: 12px 20px; }
</style>
